import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { LoginFlow, Session, UpdateLoginFlowBody } from '@ory/client';

import { SkeletonModule } from 'primeng/skeleton';
import { VKratosFormFieldDirective } from 'src/app/core/components/kratos-form/form-slot.directive';
import { KratosFormV2Component } from 'src/app/core/components/kratos-form/form.component';
import { SharedModule } from 'src/app/core/shared.module';
import { AuthRedirectService } from 'src/app/services/authentication/auth-redirect.service';
import {
  ErrBrowserLocationChangeRequired,
  ErrSecurityCsrfViolation,
  ErrSelfServiceFlowExpired,
  ErrTwoFARequired,
} from 'src/app/services/authentication/kratos.errors';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import {
  isNewlyRegistered,
  signInMethod,
} from 'src/app/services/authentication/sign-in-method';
import { ToastService } from 'src/app/services/toast/toast.service';
import { OAuth2ConfirmLoginChallengeAction } from 'src/app/store/oauth2/oauth2.actions';
import {
  TrackUserLoggedInEventAction,
  TrackUserRegisteredEventAction,
} from 'src/app/store/tracking/tracking.actions';
import { environment } from 'src/environments/environment';
import { VCardComponent } from '../../../core/components/ui-kit/card/card.component';
import { VBottomGradientWrapperComponent } from '../components/bottom-gradient/bottom-gradient.component';
import { VLogoHeaderComponent } from '../components/logo-header/logo-header.component';

@Component({
  selector: 'app-login-page',
  templateUrl: './login.component.html',
  imports: [
    SharedModule,
    VCardComponent,
    SkeletonModule,
    RouterModule,
    VKratosFormFieldDirective,
    KratosFormV2Component,
    VBottomGradientWrapperComponent,
    VLogoHeaderComponent,
  ],
})
export class LoginPageComponent implements OnInit {
  private auth = inject(KratosAuthenticationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private toast = inject(ToastService);
  private authRedirect = inject(AuthRedirectService);
  private destroyRef = inject(DestroyRef);

  aal: 'aal1' | 'aal2' = 'aal1';
  returnTo = '/';

  loginFlow?: LoginFlow;
  twoFAFlow?: LoginFlow;

  initialFlowLoading = true;

  isOAuth2Flow = false;
  oauth2LoginChallenge?: string;

  // when a user logs in with sso he will be redirected to kratos self service first
  // to complete the login flow. After that he will be redirected back to our login page.
  // the check parameter is used to check if the user is then logged in.
  oauth2ReturnUrl = `${environment.frontendUrl}/login?check=whoami`;

  isCompletingLoginFlow = signal(false);
  isCompletingTwoFAFlow = false;

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      // redirect to returnTo parameter after successful login
      if (params['returnTo']) {
        this.returnTo = params['returnTo'];
      }

      if (params['login_challenge']) {
        this.isOAuth2Flow = true;
        this.oauth2LoginChallenge = params['login_challenge'];

        // in case we are acting as an oauth2 provider we need to persist the login_challenge
        // accross the redirection process to the kratos self service
        this.oauth2ReturnUrl = `${environment.frontendUrl}/login?login_challenge=${params['login_challenge']}&check=whoami`;
      }

      if (params['check'] === 'whoami') {
        this.auth.setSessionFromWhoami().then(session => {
          this.onLoginCompleted(session);
        });
      } else if (
        params['login_challenge'] &&
        !params['flow'] &&
        !params['aal']
      ) {
        // Acting as the OAuth2 provider for someone who is already signed in
        // (a status page admin sent here from the dashboard): confirm the
        // challenge from the existing session instead of showing the form.
        // Without a session (or with 2FA still due) whoami fails and the
        // normal flow takes over.
        this.auth
          .setSessionFromWhoami()
          .then(() => {
            this.store.dispatch(
              new OAuth2ConfirmLoginChallengeAction(this.oauth2LoginChallenge!)
            );
          })
          .catch(() => this.startLoginFlow(params));
      } else {
        this.startLoginFlow(params);
      }
    });
  }

  /** Loads the Kratos flow the URL asks for (existing flow, 2FA, or a fresh password flow). */
  private startLoginFlow(params: Params) {
    if (params['flow']) {
      this.auth
        .getLoginFlow(params['flow'])
        .then(flow => {
          if (flow.requested_aal === 'aal2') {
            this.aal = 'aal2';
            this.twoFAFlow = flow;

            // when the user has enabled two factor authentication kratos self service
            // will redirect to /login?flow=xxx as the login flow is not yet completed.
            // however in this case there will be a return_to parameter which contains the
            // original oauth2 login challenge. we switch to the oauth2 flow in this case.
            if (flow.return_to) {
              const url = new URL(flow.return_to);
              const loginChallenge = url.searchParams.get('login_challenge');
              if (loginChallenge) {
                this.isOAuth2Flow = true;
                this.oauth2LoginChallenge = loginChallenge;
              }
            }
          } else {
            this.loginFlow = flow;
          }

          this.initialFlowLoading = false;
        })
        .catch(() => {
          this.router.navigate(['/login']);
        });
    } else {
      // check wether we need assurance level 1 (password) or level 2 (two factor)
      if (params['aal'] && params['aal'] === 'aal2') {
        this.aal = 'aal2';

        // get two factor flow
        this.auth.createLoginFlow('aal2').then(twoFAFlow => {
          this.initialFlowLoading = false;
          this.twoFAFlow = twoFAFlow;
        });
      } else {
        // get password flow
        this.auth.createLoginFlow('aal1', this.oauth2ReturnUrl).then(flow => {
          this.initialFlowLoading = false;
          this.loginFlow = flow;
        });
      }
    }
  }

  async submitFlow(data: UpdateLoginFlowBody) {
    if (this.isCompletingLoginFlow()) {
      return;
    }
    this.isCompletingLoginFlow.set(true);

    let isRedirecting = false;
    await this.auth
      .completeLoginFlowV2(this.loginFlow!, data.method, data)
      .then(session => {
        this.onLoginCompleted(session);
      })
      .catch(err => {
        if (err instanceof ErrTwoFARequired) {
          this.router.navigate(['/login'], { queryParams: { aal: 'aal2' } });
        } else if (err instanceof ErrBrowserLocationChangeRequired) {
          isRedirecting = true;
          this.authRedirect
            .redirect(err.redirectUri)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.isCompletingLoginFlow.set(false);
            });
        } else if (
          err instanceof ErrSecurityCsrfViolation ||
          err instanceof ErrSelfServiceFlowExpired
        ) {
          this.toast.showInfo(
            'pages.login.errors.sessionExpired.header',
            'pages.login.errors.sessionExpired.body'
          );
        } else {
          this.loginFlow = err.response.data;
        }
      })
      .finally(() => {
        // stay busy while the browser leaves for the provider: another
        // provider submit now would invalidate this redirect's OIDC state
        if (!isRedirecting) {
          this.isCompletingLoginFlow.set(false);
        }
      });
  }

  async submitTwoFAFlow(data: UpdateLoginFlowBody) {
    this.isCompletingTwoFAFlow = true;
    await this.auth
      .completeLoginFlowV2(this.twoFAFlow!, data.method, data)
      .then(session => {
        this.onLoginCompleted(session);
      })
      .catch(err => {
        this.twoFAFlow = err.response.data;
      })
      .finally(() => {
        this.isCompletingTwoFAFlow = false;
      });
  }

  onLoginCompleted(session: Session) {
    const method = signInMethod(session);

    // An OIDC sign-up creates the identity together with the session; the
    // action=register return URL alone also comes back for existing accounts.
    if (isNewlyRegistered(session)) {
      this.store.dispatch(
        new TrackUserRegisteredEventAction(session.identity!.id, method)
      );
    }

    this.store.dispatch(
      new TrackUserLoggedInEventAction(session.identity!.id, method)
    );

    if (this.isOAuth2Flow) {
      this.store.dispatch(
        new OAuth2ConfirmLoginChallengeAction(this.oauth2LoginChallenge!)
      );
    } else {
      this.router.navigate([this.returnTo]);
    }
  }
}
